/**
 * Extraction Progress Component
 * 
 * Shows real-time progress for background extraction jobs.
 * Polls job status every 3 seconds until completion.
 */

import React, { useState, useEffect, useRef } from 'react';
import JobTracker from '../utils/JobTracker';
import './ExtractionProgress.css';

export default function ExtractionProgress({ jobId, onComplete, onCancel }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const trackerRef = useRef(null);

  useEffect(() => {
    // Skip tracking if jobId is a temporary placeholder state
    if (!jobId || jobId === "uploading" || jobId === "starting") {
      return;
    }
    
    // Create job tracker
    const tracker = new JobTracker(jobId, {
      // onProgress callback
      onProgress: (jobData) => {
        setJob(jobData);
        setError(null);
      },
      
      // onComplete callback
      onComplete: (jobData) => {
        setJob(jobData);
        setError(null);
        
        // Show success state briefly before calling parent onComplete
        setTimeout(() => {
          if (onComplete) {
            onComplete(jobData);
          }
        }, 1500);
      },
      
      // onError callback
      onError: (errorMessage) => {
        setError(errorMessage);
        
        // Notify parent after showing error
        setTimeout(() => {
          if (onCancel) {
            onCancel();
          }
        }, 3000);
      }
    });

    trackerRef.current = tracker;
    tracker.start();

    // Cleanup on unmount
    return () => {
      if (trackerRef.current) {
        trackerRef.current.stop();
      }
    };
  }, [jobId, onComplete, onCancel]);

  // Show error state
  if (error) {
    return (
      <div className="extraction-progress error">
        <div className="error-icon">❌</div>
        <h3>Extraction Failed</h3>
        <p className="error-message">{error}</p>
        <button onClick={onCancel} className="btn-secondary">
          Close
        </button>
      </div>
    );
  }

  // Show uploading state
  if (jobId === "uploading") {
    return (
      <div className="extraction-progress">
        <div className="spinner"></div>
        <h3>Uploading Files</h3>
        <p>Preparing your documents for processing...</p>
      </div>
    );
  }
  
  // Show loading state
  if (!job) {
    return (
      <div className="extraction-progress">
        <div className="spinner"></div>
        <p>Starting extraction...</p>
      </div>
    );
  }

  // Determine which stage icons to show as active/done
  const getStageClass = (stageName) => {
    if (job.stage === stageName) return 'stage-active';
    
    const stageOrder = ['uploading', 'extracting', 'normalizing', 'building', 'complete'];
    const currentIndex = stageOrder.indexOf(job.stage);
    const stageIndex = stageOrder.indexOf(stageName);
    
    if (stageIndex < currentIndex) return 'stage-done';
    return '';
  };

  return (
    <div className="extraction-progress">
      {/* Header */}
      <h3>Processing Your Documents</h3>
      
      {/* Progress Bar */}
      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill"
          style={{ width: `${job.progress_percent}%` }}
        >
          <span className="progress-text">{job.progress_percent}%</span>
        </div>
      </div>
      
      {/* Stage Indicators */}
      <div className="stage-indicators">
        <div className={`stage ${getStageClass('extracting')}`}>
          <div className="stage-icon">📄</div>
          <div className="stage-label">Extracting</div>
        </div>
        
        <div className="stage-connector"></div>
        
        <div className={`stage ${getStageClass('normalizing')}`}>
          <div className="stage-icon">🔄</div>
          <div className="stage-label">Processing</div>
        </div>
        
        <div className="stage-connector"></div>
        
        <div className={`stage ${getStageClass('building')}`}>
          <div className="stage-icon">📝</div>
          <div className="stage-label">Building</div>
        </div>
        
        <div className="stage-connector"></div>
        
        <div className={`stage ${getStageClass('complete')}`}>
          <div className="stage-icon">✅</div>
          <div className="stage-label">Complete</div>
        </div>
      </div>
      
      {/* Current Step Detail */}
      <div className="current-step">
        <div className="step-icon">⏳</div>
        <div className="step-text">{job.current_step}</div>
      </div>
      
      {/* Files Progress */}
      {job.files_total > 0 && (
        <div className="files-progress">
          <span className="files-label">Files processed:</span>
          <span className="files-count">
            {job.files_processed} / {job.files_total}
          </span>
        </div>
      )}
      
      {/* Estimated Time Remaining */}
      {job.estimated_time_remaining && job.estimated_time_remaining > 0 && (
        <div className="time-remaining">
          <span className="time-icon">⏱️</span>
          <span className="time-text">
            Estimated time: {Math.ceil(job.estimated_time_remaining / 60)} minutes
          </span>
        </div>
      )}
      
      {/* Success State */}
      {job.status === 'completed' && (
        <div className="completion-message">
          <div className="success-icon">🎉</div>
          <p>Opinion generated successfully!</p>
        </div>
      )}
    </div>
  );
}
